"use client";

import {
  createContext,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface ListingQuoteSummary {
  totalCents: number;
  quantity: number;
}

interface ListingQuoteSummaryContextValue {
  summary: ListingQuoteSummary | null;
  setSummary: (summary: ListingQuoteSummary | null) => void;
  canBuy: boolean;
  setCanBuy: (canBuy: boolean) => void;
  actionLabel: string;
  setActionLabel: (label: string) => void;
  buyRef: React.RefObject<(() => void) | null>;
}

const DEFAULT_ACTION_LABEL = "Buy now";

const ListingQuoteSummaryContext =
  createContext<ListingQuoteSummaryContextValue>({
    summary: null,
    setSummary: () => {},
    canBuy: false,
    setCanBuy: () => {},
    actionLabel: DEFAULT_ACTION_LABEL,
    setActionLabel: () => {},
    buyRef: { current: null },
  });

export function ListingQuoteSummaryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [summary, setSummary] = useState<ListingQuoteSummary | null>(null);
  const [canBuy, setCanBuy] = useState(false);
  const [actionLabel, setActionLabel] = useState(DEFAULT_ACTION_LABEL);
  const buyRef = useRef<(() => void) | null>(null);
  const value = useMemo(
    () => ({
      summary,
      setSummary,
      canBuy,
      setCanBuy,
      actionLabel,
      setActionLabel,
      buyRef,
    }),
    [summary, canBuy, actionLabel],
  );
  return (
    <ListingQuoteSummaryContext value={value}>
      {children}
    </ListingQuoteSummaryContext>
  );
}

export function useListingQuoteSummary() {
  const { summary, canBuy, actionLabel, buyRef } = use(
    ListingQuoteSummaryContext,
  );
  const buy = useCallback(() => buyRef.current?.(), [buyRef]);
  return { summary, canBuy, actionLabel, buy };
}

export function usePublishListingQuote(
  totalCents: number | null,
  quantity: number,
  actionLabel: string,
  buy: () => void,
) {
  const { setSummary, setCanBuy, setActionLabel, buyRef } = use(
    ListingQuoteSummaryContext,
  );

  useEffect(() => {
    buyRef.current = buy;
  });

  useEffect(() => {
    setCanBuy(true);
    return () => {
      buyRef.current = null;
      setCanBuy(false);
      setSummary(null);
      setActionLabel(DEFAULT_ACTION_LABEL);
    };
  }, [buyRef, setCanBuy, setSummary, setActionLabel]);

  useEffect(() => {
    setActionLabel(actionLabel);
  }, [setActionLabel, actionLabel]);

  useEffect(() => {
    setSummary(totalCents === null ? null : { totalCents, quantity });
  }, [setSummary, totalCents, quantity]);
}
